import { formatLabel } from './formatLabel.ts';
import { formatProduct } from './formatProduct.ts';
import { getProductAppIcon } from './getProductAppIcon.ts';
import { getSmartLinkIcon } from './smartLinkIcon.ts';
import type { EnhancedSmartLink, SmartLinkIcon } from './smartLinkTypes.ts';

export const MAX_BYLINE_CONNECTION_ICONS = 3;

export type BylineConnectionIcon = {
	key: string;
	label: string;
	icon: SmartLinkIcon;
};

type RankedBylineConnectionIcon = {
	icon: BylineConnectionIcon;
	isUnconnected: boolean;
	occurrenceIndex: number;
};

const isUnconnectedSmartLink = (link: EnhancedSmartLink): boolean => link.access !== 'granted';

const toIcon = (link: EnhancedSmartLink, icon: SmartLinkIcon): BylineConnectionIcon => {
	const product = formatLabel(link.product);
	const isAppLogo = icon.kind === 'logo';

	return {
		// App logos collapse Jira / Jira Software and Atlas apps onto one chip.
		key: isAppLogo ? icon.key : product || icon.key,
		label: (isAppLogo ? formatProduct(link.product) : product) ?? icon.label ?? '',
		icon,
	};
};

const dedupeIcons = (icons: BylineConnectionIcon[]): BylineConnectionIcon[] => {
	const seenKeys = new Set<string>();
	const dedupedIcons: BylineConnectionIcon[] = [];

	for (const icon of icons) {
		if (!seenKeys.has(icon.key)) {
			seenKeys.add(icon.key);
			dedupedIcons.push(icon);
		}
	}

	return dedupedIcons;
};

const sortRankedIcons = (left: RankedBylineConnectionIcon, right: RankedBylineConnectionIcon) => {
	if (left.isUnconnected !== right.isUnconnected) {
		return Number(right.isUnconnected) - Number(left.isUnconnected);
	}

	return left.occurrenceIndex - right.occurrenceIndex;
};

// Ranks one source: unconnected links first (they carry a connect prompt), then document or
// recency order.
const rankIcons = (links: readonly EnhancedSmartLink[]): BylineConnectionIcon[] => {
	const rankedIcons: RankedBylineConnectionIcon[] = [];

	for (const link of links) {
		const icon = getProductAppIcon(link) ?? getSmartLinkIcon(link);
		if (!icon) {
			continue;
		}

		rankedIcons.push({
			icon: toIcon(link, icon),
			isUnconnected: isUnconnectedSmartLink(link),
			occurrenceIndex: link.occurrenceIndex,
		});
	}

	rankedIcons.sort(sortRankedIcons);
	return rankedIcons.map(({ icon }) => icon);
};

export type BylineConnectionIconSources = {
	additionalIcons?: readonly BylineConnectionIcon[];
	relatedContentLinks?: readonly EnhancedSmartLink[];
	incomingLinks?: readonly EnhancedSmartLink[];
	outgoingLinks?: readonly EnhancedSmartLink[];
};

// Dev Docs, then Related content, Appears in and Links to, the order the panel lists them in
// (CCINT-9503). Each later source only takes the slots left over, deduped by product across all.
const getRankedSourceIcons = ({
	additionalIcons = [],
	relatedContentLinks = [],
	incomingLinks = [],
	outgoingLinks = [],
}: BylineConnectionIconSources): BylineConnectionIcon[][] => [
	[...additionalIcons],
	rankIcons(relatedContentLinks),
	rankIcons(incomingLinks),
	rankIcons(outgoingLinks),
];

export const getBylineConnectionIcons = (
	sources: BylineConnectionIconSources,
): BylineConnectionIcon[] =>
	dedupeIcons(getRankedSourceIcons(sources).flat()).slice(0, MAX_BYLINE_CONNECTION_ICONS);

export type BylineConnectionIconLoadingState = {
	relatedContentLoading: boolean;
	incomingLinksLoading: boolean;
	outgoingLinksLoading: boolean;
};

// The stack must not change after it paints, but the sources resolve independently. Because a later
// source only fills leftover slots, the stack is final once the sources ahead of the first pending
// one fill it on their own, or once nothing is pending.
export const isBylineConnectionIconStackFinal = (
	sources: BylineConnectionIconSources,
	{
		relatedContentLoading,
		incomingLinksLoading,
		outgoingLinksLoading,
	}: BylineConnectionIconLoadingState,
): boolean => {
	const [devDocsIcons, ...linkSourceIcons] = getRankedSourceIcons(sources);
	const loading = [relatedContentLoading, incomingLinksLoading, outgoingLinksLoading];
	const settledIcons = [...devDocsIcons];

	for (const [index, icons] of linkSourceIcons.entries()) {
		if (dedupeIcons(settledIcons).length >= MAX_BYLINE_CONNECTION_ICONS) {
			return true;
		}
		if (loading[index]) {
			return false;
		}
		settledIcons.push(...icons);
	}

	return true;
};
